import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Lightbulb, BookOpen, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ResearchLink {
  title: string;
  url: string;
}

interface WhyThisMattersCardProps {
  toolName: string;
  shortDescription: string;
  whyItMatters: string;
  researchSummary: string;
  howToOptimise?: string;
  researchLinks?: ResearchLink[];
  tags?: string[];
}

export default function WhyThisMattersCard({
  toolName,
  shortDescription,
  whyItMatters,
  researchSummary,
  howToOptimise,
  researchLinks = [],
  tags = []
}: WhyThisMattersCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="p-4 space-y-3 bg-primary/5 border-primary/20">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <Lightbulb className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="font-semibold text-sm">Why This Matters</h4>
            <p className="text-sm text-muted-foreground">{shortDescription}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="shrink-0"
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </div>

      {isExpanded && (
        <div className="space-y-4 pt-2 border-t">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">The Science</span>
            </div>
            <p className="text-sm text-muted-foreground">{whyItMatters}</p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">Research Shows</span>
            </div>
            <p className="text-sm text-muted-foreground">{researchSummary}</p>
          </div>

          {howToOptimise && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold">💡 Optimisation Tips</span>
              </div>
              <p className="text-sm text-muted-foreground">{howToOptimise}</p>
            </div>
          )}

          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {researchLinks && researchLinks.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-muted-foreground block mb-2">
                Research References:
              </span>
              <div className="space-y-1">
                {researchLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span>{link.title}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
